// Kata Coaching Page Component

import React, { useEffect, useState } from 'react';
import { useKataCoachingStore } from '../stores/kataCoachingStore';
import SearchableWorkshopDropdown from '../components/common/SearchableWorkshopDropdown';
import SearchableUserDropdown from '../components/common/SearchableUserDropdown';
import { WorkshopService } from '../services/workshop';
import { UserService } from '../services/user';
import { KataCoachingService } from '../services/kataCoaching';
import { User } from '../services/user';
import { Workshop } from '../types/api';
import { KataCoachingSession } from '../services/kataCoaching';
import { Eye, Edit, Trash2, Upload, Paperclip } from 'lucide-react';
import CustomWarningAlert from '../components/common/CustomWarningAlert';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useAuthStore } from '../stores/authStore';
import { useDashboardStore } from '../stores/dashboardStore';

const KataCoachingPage: React.FC = () => {
  const { 
    formData, 
    setFormData, 
    createSession, 
    calculateTotalScore, 
    calculateTotalScorePercentage,
    calculateCompletionRate,
    isLoading,
    isCreating,
    error,
    resetForm,
    sessions,
    fetchSessions,
    filters,
    setFilters
  } = useKataCoachingStore();
  const { user, accessContext } = useAuthStore();
  const { alvId } = useDashboardStore();

  const canCreateSessions = Boolean(
    user?.is_admin ||
    (alvId
      ? accessContext?.accessible_plants.some((plant) => {
          const value = plant.alv_id || plant.code || plant.name;
          return value === alvId && plant.can_edit === true;
        })
      : accessContext?.accessible_plants.some((plant) => plant.can_edit === true))
  );

  // Local state for dropdowns and tabs
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState<'list' | 'create'>('list');
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<KataCoachingSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [deleteAttachmentConfirmOpen, setDeleteAttachmentConfirmOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<number | null>(null);
  const editableWorkshops = workshops.filter((workshop) => workshop.can_edit === true);

  // Load workshops, users, and coaching sessions on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const [workshopsResult, usersResult, sessionsResult] = await Promise.allSettled([
          WorkshopService.getWorkshops({ page_size: 1000 }),
          UserService.getAllUsers(),
          fetchSessions()
        ]);

        if (workshopsResult.status === 'fulfilled') {
          setWorkshops(workshopsResult.value.results);
          console.log('Loaded workshops:', workshopsResult.value.results.length);
        } else {
          console.error('Failed to load workshops:', workshopsResult.reason);
        }

        if (usersResult.status === 'fulfilled') {
          setUsers(usersResult.value);
          console.log('Loaded users:', usersResult.value.length);
        } else {
          // User selectors are optional for viewing existing sessions.  A
          // directory failure must not blank the coaching page.
          console.error('Failed to load user directory:', usersResult.reason);
          setUsers([]);
        }

        if (sessionsResult.status === 'rejected') {
          console.error('Failed to load coaching sessions:', sessionsResult.reason);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [fetchSessions]);

  useEffect(() => {
    if (!canCreateSessions && activeTab === 'create') {
      setActiveTab('list');
    }
  }, [canCreateSessions, activeTab]);

  // 10 Questions for Kata Coaching
  const QUESTIONS = [
    "What is the Target Condition? / Quel est Votre Cible?",
    "What was your last step? / Quelle est la situation actuelle?",
    "What actually happened? / Que s'est il passé factuellement?",
    "What did you learn? / Qu'avez-vous appris?",
    "What obstacles are now preventing you from reaching the target condition? / Quels sont selon vous les obstacles qui vous empechent d'atteindre votre objectif?",
    "Which obstacle are you addressing now? Why? / Lequel de ces obstacles allez vous considérer? Pourquoi?",
    "What is the cause of this obstacle? / Quelle est la cause de cet obstacle?",
    "What is your next step? (next PDCA/ Experiment) / Quelle est donc votre prochaine étape? PDCA Experimentation? Gemba sur process",
    "What do you expect? / Quelle sont vos attentes?",
    "When can we go and see what we have learned from taking that step? / Quand est ce qu'on peux se rencontrer pour verifier ce que vous avez appris?"
  ];

  const handleWorkshopSelect = async (workshopId: number) => {
    try {
      console.log('Selected workshop ID:', workshopId);
      
      // Fetch workshop details to get mentor and mentees names
      const workshop = await WorkshopService.getWorkshop(workshopId);
      console.log('Fetched workshop:', workshop);
      console.log('Workshop mentor:', workshop.mentor);
      console.log('Workshop mentees:', workshop.mentees);
      
      // Prepare the form data updates - always reset mentor and mentees first
      const updates: any = { 
        workshop: workshopId,
        coach_mentor: undefined,  // Reset mentor field
        mentees: []               // Reset mentees field
      };
      
      // Auto-fill coach/mentor if mentor exists
      if (workshop.mentor) {
        console.log('Looking for mentor:', workshop.mentor, typeof workshop.mentor);
        
        if (typeof workshop.mentor === 'number' || (typeof workshop.mentor === 'string' && !isNaN(Number(workshop.mentor)))) {
          // Case 1: Mentor is a user ID (number or numeric string)
          const mentorId = typeof workshop.mentor === 'number' ? workshop.mentor : Number(workshop.mentor);
          console.log('Mentor is a user ID:', mentorId);
          const mentorUser = users.find((user: User) => user.id === mentorId);
          console.log('Found mentor user by ID:', mentorUser);
          if (mentorUser) {
            updates.coach_mentor = mentorUser.id;
            console.log('Will set coach_mentor to user ID:', mentorUser.id);
          }
          
        } else if (typeof workshop.mentor === 'string') {
          // Case 2: Mentor is a name (string)
          console.log('Mentor is a name (string)');
        const mentorUser = users.find((user: User) => 
          user.full_name.toLowerCase().includes(workshop.mentor!.toLowerCase()) ||
          user.username.toLowerCase().includes(workshop.mentor!.toLowerCase())
        );
          console.log('Found mentor user by name:', mentorUser);
          if (mentorUser) {
            updates.coach_mentor = mentorUser.id;
            console.log('Will set coach_mentor to:', mentorUser.id);
          }
        } else {
          console.log('Unknown mentor type:', typeof workshop.mentor, workshop.mentor);
        }
      }
      
      // Auto-fill mentees if mentees exist
      if (workshop.mentees && Array.isArray(workshop.mentees) && workshop.mentees.length > 0) {
        console.log('Processing mentees:', workshop.mentees);
        
        // Check if mentees are user IDs (numbers) or names (strings)
        const firstMentee = workshop.mentees[0];
        console.log('First mentee type:', typeof firstMentee, firstMentee);
        
        if (typeof firstMentee === 'number' || (typeof firstMentee === 'string' && !isNaN(Number(firstMentee)))) {
          // Case 1: Mentees are user IDs (numbers or numeric strings)
          console.log('Mentees are user IDs');
          const validUserIds = workshop.mentees
            .filter(id => typeof id === 'number' || (typeof id === 'string' && !isNaN(Number(id))))
            .map(id => typeof id === 'number' ? id : Number(id));
          console.log('Valid user IDs:', validUserIds);
          
          // Verify these user IDs exist in our users list
          const existingUserIds = validUserIds.filter(id => 
            users.some((user: User) => user.id === id)
          );
          console.log('Existing user IDs:', existingUserIds);
          
          if (existingUserIds.length > 0) {
            updates.mentees = existingUserIds;
            console.log('Will set mentees to user IDs:', existingUserIds);
          }
          
        } else if (typeof firstMentee === 'string') {
          // Case 2: Mentees are names (strings) - handle newline-separated names
          console.log('Mentees are names (strings)');
        const menteeNames = workshop.mentees.reduce((acc: string[], menteeString: any) => {
          if (typeof menteeString !== 'string') {
            console.log('Non-string mentee found:', menteeString, typeof menteeString);
            return acc;
          }
          const names = menteeString.split('\n').map(name => name.trim()).filter(name => name.length > 0);
          return acc.concat(names);
        }, []);
          console.log('Parsed mentee names:', menteeNames);
          
        const menteeUsers = users.filter((user: User) => 
          menteeNames.some((menteeName: any) => {
            // Ensure menteeName is a string before calling toLowerCase
            if (typeof menteeName !== 'string') {
              console.log('Non-string mentee name found:', menteeName, typeof menteeName);
              return false;
            }
            return user.full_name.toLowerCase().includes(menteeName.toLowerCase()) ||
                   user.username.toLowerCase().includes(menteeName.toLowerCase());
          })
        );
          console.log('Found mentee users:', menteeUsers);
          
          if (menteeUsers.length > 0) {
            updates.mentees = menteeUsers.map((user: User) => user.id);
            console.log('Will set mentees to:', menteeUsers.map((user: User) => user.id));
          }
        } else {
          console.log('Unknown mentee type:', typeof firstMentee, firstMentee);
        }
      }
      
      // Update form data once with all changes
      console.log('Updating form data with:', updates);
      setFormData(updates);
      
    } catch (error) {
      console.error('Failed to load workshop details:', error);
      // Still set the workshop ID even if auto-fill fails
      setFormData({ workshop: workshopId });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createSession();
      resetForm();
      // Redirect to coaching list tab after successful creation
      setActiveTab('list');
      // Refresh the sessions list to show the new session
      await fetchSessions();
    } catch (error) {
      console.error('Failed to create coaching session:', error);
    }
  };

  // Action handlers
  const handleViewSession = async (sessionId: number) => {
    try {
      const session = await KataCoachingService.getSession(sessionId);
      setSelectedSession(session);
      setViewModalOpen(true);
    } catch (error) {
      console.error('Failed to load session details:', error);
    }
  };

  const handleEditSession = async (sessionId: number) => {
    try {
      const session = await KataCoachingService.getSession(sessionId);
      setSelectedSession(session);
      setEditModalOpen(true);
    } catch (error) {
      console.error('Failed to load session details:', error);
    }
  };

  const handleDeleteSession = (sessionId: number) => {
    const session = sessions.find((s: KataCoachingSession) => s.id === sessionId);
    setSelectedSession(session || null);
    setDeleteModalOpen(true);
  };

  const confirmDeleteSession = async () => {
    if (!selectedSession?.id) return;
    
    try {
      setIsDeleting(true);
      await KataCoachingService.deleteSession(selectedSession.id);
      await fetchSessions(); // Refresh the list
      setDeleteModalOpen(false);
      setSelectedSession(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadAttachment = async (sessionId: number) => {
    try {
      const session = sessions.find((s: KataCoachingSession) => s.id === sessionId);
      setSelectedSession(session || null);
      setUploadModalOpen(true);
      // Load existing attachments
      await loadAttachments(sessionId);
    } catch (error) {
      console.error('Failed to load session for upload:', error);
    }
  };

  const loadAttachments = async (sessionId: number) => {
    try {
      setLoadingAttachments(true);
      const atts = await KataCoachingService.getAttachments(sessionId);
      setAttachments(atts);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedSession?.id || !selectedFile) return;

    try {
      setIsUploading(true);
      await KataCoachingService.uploadAttachment(selectedSession.id, selectedFile);
      // Reload attachments
      await loadAttachments(selectedSession.id);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('attachment-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      alert('Failed to upload attachment. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = (attachmentId: number) => {
    setAttachmentToDelete(attachmentId);
    setDeleteAttachmentConfirmOpen(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!selectedSession?.id || !attachmentToDelete) return;

    try {
      await KataCoachingService.deleteAttachment(selectedSession.id, attachmentToDelete);
      await loadAttachments(selectedSession.id);
      setDeleteAttachmentConfirmOpen(false);
      setAttachmentToDelete(null);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      alert('Failed to delete attachment. Please try again.');
      setDeleteAttachmentConfirmOpen(false);
      setAttachmentToDelete(null);
    }
  };

  const totalScore = calculateTotalScore();
  const totalScorePercentage = calculateTotalScorePercentage();
  const completionRate = calculateCompletionRate();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kata Coaching Sessions</h1>
            <p className="mt-2 text-gray-600">
              {canCreateSessions
                ? 'Manage and create coaching sessions with 10 questions scoring'
                : 'View coaching sessions for your accessible plants'}
            </p>
          </div>
          <div>
            <DateRangeFilter
              startDate={filters.deadline_from}
              endDate={filters.deadline_to}
              onStartDateChange={(date) => setFilters({ deadline_from: date })}
              onEndDateChange={(date) => setFilters({ deadline_to: date })}
              onClear={() => setFilters({ deadline_from: undefined, deadline_to: undefined })}
              label="Filter by Workshop Deadline"
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('list')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'list'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Coaching Sessions ({sessions.length})
              </button>
              {canCreateSessions && (
              <button
                onClick={() => setActiveTab('create')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'create'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Create New Session
              </button>
              )}
            </nav>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'list' ? (
          /* Coaching Sessions Table */
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900">Existing Coaching Sessions</h2>
              {!canCreateSessions && (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 border border-amber-200">
                  View only
                </span>
              )}
            </div>
            
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Loading coaching sessions...</p>
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No coaching sessions found.</p>
                {canCreateSessions && (
                <button
                  onClick={() => setActiveTab('create')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create First Session
                </button>
                )}
              </div>
            ) : (
              <div>
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <colgroup>
                    <col className="w-[32%]" />
                    <col className="w-[16%]" />
                    <col className="w-[18%]" />
                    <col className="w-[10%]" />
                    <col className="w-[8%]" />
                    <col className="w-[16%]" />
                  </colgroup>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workshop</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coach</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mentees</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sessions.map((session: KataCoachingSession) => {
                      const workshopTitle = session.workshop_title || `Workshop ${session.workshop}`;
                      const coachName = session.coach_name || `User ${session.coach_mentor}`;
                      const menteesLabel = session.mentees_names && session.mentees_names.length > 0
                        ? session.mentees_names.join(', ')
                        : '';
                      return (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 min-w-0">
                          <div className="group relative min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {workshopTitle}
                            </div>
                            <div
                              role="tooltip"
                              className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden max-w-sm rounded-md bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block"
                            >
                              {workshopTitle}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 min-w-0">
                          <div className="text-sm text-gray-900 truncate" title={coachName}>
                            {coachName}
                          </div>
                        </td>
                        <td className="px-3 py-3 min-w-0">
                          {menteesLabel ? (
                            <div className="group relative min-w-0">
                              <div className="text-sm text-gray-900 truncate">
                                {menteesLabel}
                              </div>
                              <div
                                role="tooltip"
                                className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden max-w-sm rounded-md bg-gray-900 px-2 py-1 text-xs font-normal text-white shadow-lg group-hover:block"
                              >
                                {menteesLabel}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No mentees</span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(session.coaching_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {session.total_score || 0}/50
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm font-medium">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewSession(session.id!)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View Session"
                            >
                              <Eye size={16} />
                            </button>
                            {session.can_edit && (
                              <>
                            <button
                              onClick={() => handleEditSession(session.id!)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                              title="Edit Session"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleUploadAttachment(session.id!)}
                              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                              title="Upload Attachment"
                            >
                              <Upload size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteSession(session.id!)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Delete Session"
                            >
                              <Trash2 size={16} />
                            </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* Create New Session Form */
          <form onSubmit={handleSubmit} className="space-y-8">
          {/* Session Details Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Session Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Workshop Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Workshop <span className="text-red-500">*</span>
                </label>
                <SearchableWorkshopDropdown
                  workshops={editableWorkshops.map((w: Workshop) => ({ id: w.id, title: w.title, department_name: w.department_name || 'Unassigned' }))}
                  selectedWorkshops={formData.workshop ? [formData.workshop] : []}
                  onSelectionChange={(selectedIds: number[]) => {
                    if (selectedIds.length > 0) {
                      handleWorkshopSelect(selectedIds[0]);
                    } else {
                      // Clear all fields when workshop is deselected
                      setFormData({ 
                        workshop: undefined,
                        coach_mentor: undefined,
                        mentees: []
                      });
                    }
                  }}
                  placeholder="Search and select workshop..."
                  multiple={false}
                  disabled={loadingData || isLoading}
                />
              </div>
              
              {/* Coach/Mentor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coach / Mentor <span className="text-red-500">*</span>
                </label>
                <SearchableUserDropdown
                  users={users}
                  selectedUsers={formData.coach_mentor ? [formData.coach_mentor] : []}
                  onSelectionChange={(selectedIds: number[]) => {
                    setFormData({ coach_mentor: selectedIds.length > 0 ? selectedIds[0] : undefined });
                  }}
                  placeholder="Search and select coach/mentor..."
                  multiple={false}
                  disabled={loadingData || isLoading}
                />
              </div>
              
              {/* Mentees Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mentee <span className="text-red-500">*</span>
                </label>
                <SearchableUserDropdown
                  users={users}
                  selectedUsers={formData.mentees || []}
                  onSelectionChange={(selectedIds: number[]) => {
                    setFormData({ mentees: selectedIds });
                  }}
                  placeholder="Search and select mentees..."
                  multiple={true}
                  disabled={loadingData || isLoading}
                />
              </div>
              
              {/* Coaching Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coaching Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.coaching_date || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ coaching_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* 10 Questions Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">10 Questions Coaching</h2>
            
            <div className="space-y-4">
              {QUESTIONS.map((question, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{question}</p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <select
                      value={formData[`question_${index + 1}_score` as keyof typeof formData] as number || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ [`question_${index + 1}_score`]: parseInt(e.target.value) })}
                      className="px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Score</option>
                      {[0, 1, 2, 3, 4, 5].map(scoreValue => (
                        <option key={scoreValue} value={scoreValue}>{scoreValue}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            
                     {/* Total Score Display */}
                     <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                       <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                         {(() => {
                           const scores = [
                             formData.question_1_score, formData.question_2_score, formData.question_3_score,
                             formData.question_4_score, formData.question_5_score, formData.question_6_score,
                             formData.question_7_score, formData.question_8_score, formData.question_9_score,
                             formData.question_10_score
                           ];
                           const validScores = scores.filter(score => score !== undefined && score !== null);
                           const totalScore = validScores.reduce((sum, score) => sum + score, 0);
                           const scorePercentage = (totalScore / 50) * 100;
                           const answeredQuestions = validScores.length;
                           const completionRate = (answeredQuestions / 10) * 100;
                           
                           // Performance thresholds
                           const getScoreColor = (score: number) => {
                             if (score >= 35) return { color: 'text-green-600', bg: 'bg-green-500' }; // 70%+ (35/50)
                             if (score >= 20) return { color: 'text-yellow-600', bg: 'bg-yellow-500' }; // 40-69% (20-34/50)
                             return { color: 'text-red-600', bg: 'bg-red-500' }; // <40% (<20/50)
                           };
                           
                           const getPercentageColor = (percentage: number) => {
                             if (percentage >= 70) return { color: 'text-green-600', bg: 'bg-green-500' };
                             if (percentage >= 40) return { color: 'text-yellow-600', bg: 'bg-yellow-500' };
                             return { color: 'text-red-600', bg: 'bg-red-500' };
                           };
                           
                           const getCompletionColor = (rate: number) => {
                             if (rate >= 80) return { color: 'text-green-600', bg: 'bg-green-500' };
                             if (rate >= 50) return { color: 'text-yellow-600', bg: 'bg-yellow-500' };
                             return { color: 'text-red-600', bg: 'bg-red-500' };
                           };
                           
                           const scoreColors = getScoreColor(totalScore);
                           const percentageColors = getPercentageColor(scorePercentage);
                           const completionColors = getCompletionColor(completionRate);
                           
                           return (
                             <>
                               <div className="text-center">
                                 <div className={`text-2xl font-bold ${scoreColors.color}`}>{totalScore}/50</div>
                                 <div className={`text-sm ${scoreColors.color} font-medium`}>Total Score</div>
                               </div>
                               <div className="text-center">
                                 <div className={`text-2xl font-bold ${percentageColors.color}`}>{scorePercentage.toFixed(1)}%</div>
                                 <div className={`text-sm ${percentageColors.color} font-medium`}>Score Percentage</div>
                               </div>
                               <div className="text-center">
                                 <div className={`text-2xl font-bold ${completionColors.color}`}>{completionRate.toFixed(1)}%</div>
                                 <div className={`text-sm ${completionColors.color} font-medium`}>Completion Rate</div>
                               </div>
                             </>
                           );
                         })()}
                       </div>
                     </div>
          </div>

          {/* Additional Information Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Additional Information</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  rows={4}
                  value={formData.notes || ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional notes about the coaching session..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coaching Lead Time (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.coaching_lead_time_minutes || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ coaching_lead_time_minutes: parseInt(e.target.value) || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Duration in minutes"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Reset Form
            </button>
            
            <button
              type="submit"
              disabled={isCreating || isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Saving...' : 'Save Session'}
            </button>
          </div>
        </form>
        )}
        
        {/* View Session Modal */}
        {viewModalOpen && selectedSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-0 border-0 w-11/12 max-w-6xl shadow-2xl rounded-xl bg-white overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold">Coaching Session Details</h3>
                    <p className="text-blue-100 mt-1">Complete session information and scoring</p>
                  </div>
                  <button
                    onClick={() => setViewModalOpen(false)}
                    className="text-white hover:text-blue-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-8">
                <div className="space-y-8">
                  {/* Session Overview Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-blue-800 ml-3">Workshop</h4>
                      </div>
                      <p className="text-gray-800 font-medium">
                        {selectedSession.workshop_details?.title || selectedSession.workshop_title || `Workshop ${selectedSession.workshop}`}
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-green-800 ml-3">Coach</h4>
                      </div>
                      <p className="text-gray-800 font-medium">
                        {selectedSession.coach_details?.full_name || selectedSession.coach_name || `User ${selectedSession.coach_mentor}`}
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-purple-800 ml-3">Date</h4>
                      </div>
                      <p className="text-gray-800 font-medium">
                        {new Date(selectedSession.coaching_date).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-orange-800 ml-3">Duration</h4>
                      </div>
                      <p className="text-gray-800 font-medium">
                        {selectedSession.coaching_lead_time_minutes ? `${selectedSession.coaching_lead_time_minutes} min` : 'Not specified'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Mentees Section */}
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                    <div className="flex items-center mb-4">
                      <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">Mentees</h4>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {selectedSession.mentees_details && selectedSession.mentees_details.length > 0 ? (
                        selectedSession.mentees_details.map((mentee: any, index: number) => (
                          <div key={index} className="flex items-center bg-white rounded-lg px-4 py-3 shadow-sm border border-indigo-200">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                              <span className="text-indigo-600 font-semibold text-sm">
                                {mentee.full_name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <span className="text-gray-800 font-medium">{mentee.full_name}</span>
                          </div>
                        ))
                      ) : selectedSession.mentees_names && selectedSession.mentees_names.length > 0 ? (
                        selectedSession.mentees_names.map((name: string, index: number) => (
                          <div key={index} className="flex items-center bg-white rounded-lg px-4 py-3 shadow-sm border border-indigo-200">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                              <span className="text-indigo-600 font-semibold text-sm">
                                {name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <span className="text-gray-800 font-medium">{name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 italic bg-white rounded-lg px-4 py-3 border border-gray-200">
                          No mentees assigned
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Questions & Scores */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center mb-6">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">Questions & Scores</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {QUESTIONS.map((question, index) => {
                        const score = selectedSession[`question_${index + 1}_score` as keyof KataCoachingSession] as number;
                        return (
                          <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-sm text-gray-700 font-medium flex-1 pr-4">{question}</span>
                              <div className="flex-shrink-0">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                  score !== undefined && score !== null 
                                    ? score >= 4 ? 'bg-green-100 text-green-800 border border-green-200' 
                                    : score >= 2 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                    : 'bg-red-100 text-red-800 border border-red-200'
                                    : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}>
                                  {score !== undefined && score !== null ? score : 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  score !== undefined && score !== null 
                                    ? score >= 4 ? 'bg-green-500' 
                                    : score >= 2 ? 'bg-yellow-500'
                                    : 'bg-red-500'
                                    : 'bg-gray-300'
                                }`}
                                style={{ width: score !== undefined && score !== null ? `${(score / 5) * 100}%` : '0%' }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Score Summary */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                    <div className="flex items-center mb-6">
                      <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">Performance Summary</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {(() => {
                        const totalScore = selectedSession.total_score || 0;
                        const scorePercentage = (selectedSession.total_score_percentage || 0) * 100;
                        const completionRate = selectedSession.completion_rate || 0;
                        
                        // Performance thresholds
                        const getScoreColor = (score: number) => {
                          if (score >= 35) return { color: 'text-green-600', bg: 'bg-green-500', border: 'border-green-200' }; // 70%+ (35/50)
                          if (score >= 20) return { color: 'text-yellow-600', bg: 'bg-yellow-500', border: 'border-yellow-200' }; // 40-69% (20-34/50)
                          return { color: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' }; // <40% (<20/50)
                        };
                        
                        const getPercentageColor = (percentage: number) => {
                          if (percentage >= 70) return { color: 'text-green-600', bg: 'bg-green-500', border: 'border-green-200' };
                          if (percentage >= 40) return { color: 'text-yellow-600', bg: 'bg-yellow-500', border: 'border-yellow-200' };
                          return { color: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' };
                        };
                        
                        const getCompletionColor = (rate: number) => {
                          if (rate >= 80) return { color: 'text-green-600', bg: 'bg-green-500', border: 'border-green-200' };
                          if (rate >= 50) return { color: 'text-yellow-600', bg: 'bg-yellow-500', border: 'border-yellow-200' };
                          return { color: 'text-red-600', bg: 'bg-red-500', border: 'border-red-200' };
                        };
                        
                        const scoreColors = getScoreColor(totalScore);
                        const percentageColors = getPercentageColor(scorePercentage);
                        const completionColors = getCompletionColor(completionRate);
                        
                        return (
                          <>
                            <div className={`bg-white rounded-lg p-6 shadow-sm border ${scoreColors.border} text-center`}>
                              <div className={`text-4xl font-bold ${scoreColors.color} mb-2`}>{totalScore}/50</div>
                              <div className={`text-sm ${scoreColors.color} font-medium`}>Total Score</div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                                <div 
                                  className={`${scoreColors.bg} h-2 rounded-full`} 
                                  style={{ width: `${(totalScore / 50) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className={`bg-white rounded-lg p-6 shadow-sm border ${percentageColors.border} text-center`}>
                              <div className={`text-4xl font-bold ${percentageColors.color} mb-2`}>{scorePercentage.toFixed(1)}%</div>
                              <div className={`text-sm ${percentageColors.color} font-medium`}>Score Percentage</div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                                <div 
                                  className={`${percentageColors.bg} h-2 rounded-full`} 
                                  style={{ width: `${scorePercentage}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className={`bg-white rounded-lg p-6 shadow-sm border ${completionColors.border} text-center`}>
                              <div className={`text-4xl font-bold ${completionColors.color} mb-2`}>{completionRate.toFixed(1)}%</div>
                              <div className={`text-sm ${completionColors.color} font-medium`}>Completion Rate</div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                                <div 
                                  className={`${completionColors.bg} h-2 rounded-full`} 
                                  style={{ width: `${completionRate}%` }}
                                ></div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Notes Section */}
                  {selectedSession.notes && (
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                      <div className="flex items-center mb-4">
                        <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">Session Notes</h4>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-amber-200">
                        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{selectedSession.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => setViewModalOpen(false)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Edit Session Modal */}
        {editModalOpen && selectedSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-0 border-0 w-11/12 max-w-6xl shadow-2xl rounded-xl bg-white overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold">Edit Coaching Session</h3>
                    <p className="text-indigo-100 mt-1">Update session details and scoring</p>
                  </div>
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="text-white hover:text-indigo-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-8">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await KataCoachingService.updateSession(selectedSession.id!, selectedSession);
                    await fetchSessions();
                    setEditModalOpen(false);
                  } catch (error) {
                    console.error('Failed to update session:', error);
                  }
                }} className="space-y-8">
                  {/* Session Details Section */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      Session Details
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Workshop Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Workshop <span className="text-red-500">*</span>
                        </label>
                        <SearchableWorkshopDropdown
                          workshops={editableWorkshops.map((w: Workshop) => ({ id: w.id, title: w.title, department_name: w.department_name || 'Unassigned' }))}
                          selectedWorkshops={selectedSession.workshop ? [selectedSession.workshop] : []}
                          onSelectionChange={(selectedIds: number[]) => {
                            if (selectedIds.length > 0) {
                              setSelectedSession({
                                ...selectedSession,
                                workshop: selectedIds[0]
                              } as KataCoachingSession);
                            } else {
                              setSelectedSession({
                                ...selectedSession,
                                workshop: undefined
                              } as KataCoachingSession);
                            }
                          }}
                          placeholder="Search and select workshop..."
                          multiple={false}
                          disabled={loadingData}
                        />
                      </div>
                      
                      {/* Coach/Mentor Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Coach / Mentor <span className="text-red-500">*</span>
                        </label>
                        <SearchableUserDropdown
                          users={users}
                          selectedUsers={selectedSession.coach_mentor ? [selectedSession.coach_mentor] : []}
                          onSelectionChange={(selectedIds: number[]) => {
                            setSelectedSession({
                              ...selectedSession,
                              coach_mentor: selectedIds.length > 0 ? selectedIds[0] : undefined
                            } as KataCoachingSession);
                          }}
                          placeholder="Search and select coach/mentor..."
                          multiple={false}
                          disabled={loadingData}
                        />
                      </div>
                      
                      {/* Mentee Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mentee <span className="text-red-500">*</span>
                        </label>
                        <SearchableUserDropdown
                          users={users}
                          selectedUsers={selectedSession.mentees || []}
                          onSelectionChange={(selectedIds: number[]) => {
                            setSelectedSession({
                              ...selectedSession,
                              mentees: selectedIds
                            } as KataCoachingSession);
                          }}
                          placeholder="Search and select mentees..."
                          multiple={true}
                          disabled={loadingData}
                        />
                      </div>

                      {/* Coaching Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Coaching Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={selectedSession.coaching_date || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSelectedSession({
                              ...selectedSession,
                              coaching_date: e.target.value
                            } as KataCoachingSession);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Questions and Scores Section */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      Questions & Scores
                    </h2>
                    
                    <div className="space-y-4">
                      {QUESTIONS.map((question, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                          <div className="flex-1 text-sm text-gray-700 pr-4">{question}</div>
                          
                          <div className="flex-shrink-0">
                            <select
                              value={selectedSession[`question_${index + 1}_score` as keyof KataCoachingSession] as number || ''}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                const newScore = e.target.value ? parseInt(e.target.value) : undefined;
                                setSelectedSession({
                                  ...selectedSession,
                                  [`question_${index + 1}_score`]: newScore
                                } as KataCoachingSession);
                              }}
                              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            >
                              <option value="">Select Score</option>
                              {[0, 1, 2, 3, 4, 5].map(scoreValue => (
                                <option key={scoreValue} value={scoreValue}>{scoreValue}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Total Score Display */}
                    <div className="mt-6 p-4 bg-white rounded-lg border border-green-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {(() => {
                          const scores = [
                            selectedSession.question_1_score, selectedSession.question_2_score, selectedSession.question_3_score,
                            selectedSession.question_4_score, selectedSession.question_5_score, selectedSession.question_6_score,
                            selectedSession.question_7_score, selectedSession.question_8_score, selectedSession.question_9_score,
                            selectedSession.question_10_score
                          ];
                          const validScores = scores.filter(score => score !== undefined && score !== null);
                          const totalScore = validScores.reduce((sum, score) => sum + score, 0);
                          const scorePercentage = (totalScore / 50) * 100;
                          const answeredQuestions = validScores.length;
                          const completionRate = (answeredQuestions / 10) * 100;
                          
                          // Performance thresholds
                          const getScoreColor = (score: number) => {
                            if (score >= 35) return { color: 'text-green-600', bg: 'bg-green-500' }; // 70%+ (35/50)
                            if (score >= 20) return { color: 'text-yellow-600', bg: 'bg-yellow-500' }; // 40-69% (20-34/50)
                            return { color: 'text-red-600', bg: 'bg-red-500' }; // <40% (<20/50)
                          };
                          
                          const getPercentageColor = (percentage: number) => {
                            if (percentage >= 70) return { color: 'text-green-600', bg: 'bg-green-500' };
                            if (percentage >= 40) return { color: 'text-yellow-600', bg: 'bg-yellow-500' };
                            return { color: 'text-red-600', bg: 'bg-red-500' };
                          };
                          
                          const getCompletionColor = (rate: number) => {
                            if (rate >= 80) return { color: 'text-green-600', bg: 'bg-green-500' };
                            if (rate >= 50) return { color: 'text-yellow-600', bg: 'bg-yellow-500' };
                            return { color: 'text-red-600', bg: 'bg-red-500' };
                          };
                          
                          const scoreColors = getScoreColor(totalScore);
                          const percentageColors = getPercentageColor(scorePercentage);
                          const completionColors = getCompletionColor(completionRate);
                          
                          return (
                            <>
                              <div className="text-center">
                                <div className={`text-2xl font-bold ${scoreColors.color}`}>{totalScore}/50</div>
                                <div className={`text-sm ${scoreColors.color} font-medium`}>Total Score</div>
                              </div>
                              <div className="text-center">
                                <div className={`text-2xl font-bold ${percentageColors.color}`}>{scorePercentage.toFixed(1)}%</div>
                                <div className={`text-sm ${percentageColors.color} font-medium`}>Score Percentage</div>
                              </div>
                              <div className="text-center">
                                <div className={`text-2xl font-bold ${completionColors.color}`}>{completionRate.toFixed(1)}%</div>
                                <div className={`text-sm ${completionColors.color} font-medium`}>Completion Rate</div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information Section */}
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      Additional Information
                    </h2>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes
                        </label>
                        <textarea
                          rows={4}
                          value={selectedSession.notes || ''}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            setSelectedSession({
                              ...selectedSession,
                              notes: e.target.value
                            } as KataCoachingSession);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Additional notes about the coaching session..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Coaching Lead Time (minutes)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={selectedSession.coaching_lead_time_minutes || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSelectedSession({
                              ...selectedSession,
                              coaching_lead_time_minutes: parseInt(e.target.value) || undefined
                            } as KataCoachingSession);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Duration in minutes"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setEditModalOpen(false)}
                      className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    
                    <button
                      type="submit"
                      className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        {/* Upload Attachment Modal */}
        {uploadModalOpen && selectedSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Upload Attachment - {selectedSession.workshop_title || `Workshop ${selectedSession.workshop}`}
                  </h3>
                  <button
                    onClick={() => {
                      setUploadModalOpen(false);
                      setSelectedFile(null);
                      setAttachments([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* File Upload Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      id="attachment-file-input"
                      type="file"
                      onChange={handleFileSelect}
                      className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <button
                      onClick={handleUploadFile}
                      disabled={!selectedFile || isUploading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                  {selectedFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>

                {/* Existing Attachments */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Existing Attachments</h4>
                  {loadingAttachments ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">Loading attachments...</p>
                    </div>
                  ) : attachments.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-4">No attachments yet</p>
                  ) : (
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <Paperclip className="text-gray-400" size={18} />
                            <div className="flex-1 min-w-0">
                              <a
                                href={attachment.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 truncate block"
                              >
                                {attachment.original_filename}
                              </a>
                              <p className="text-xs text-gray-500">
                                {attachment.file_size_mb} MB • Uploaded by {attachment.uploaded_by_name} • {new Date(attachment.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          {selectedSession.can_edit && (
                          <button
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="ml-4 text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete Attachment"
                          >
                            <Trash2 size={16} />
                          </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setUploadModalOpen(false);
                      setSelectedFile(null);
                      setAttachments([]);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Attachment Confirmation Modal */}
        <CustomWarningAlert
          isOpen={deleteAttachmentConfirmOpen}
          onClose={() => {
            setDeleteAttachmentConfirmOpen(false);
            setAttachmentToDelete(null);
          }}
          onConfirm={confirmDeleteAttachment}
          title="Delete Attachment"
          message="Are you sure you want to delete this attachment? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Delete Confirmation Modal */}
        {deleteModalOpen && selectedSession && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center mb-4">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900">Delete Coaching Session</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this coaching session? This action cannot be undone.
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      Workshop: {selectedSession.workshop_title || `Workshop ${selectedSession.workshop}`}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      Date: {new Date(selectedSession.coaching_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex space-x-3">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteSession}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KataCoachingPage;
